import { DataModelBuilder, SerializationContext, SerializedData } from '../../../dto/data-model.js';
import { Payload } from '../payload.js';
import { PayloadType } from '../payload-type.js';
import { MapUtils } from '../../../../utility/map-utils.js';
import { EventProfile } from '../../event-profile.js';
import { cloneDataOrNull, isSameData, cloneListOfCloneable, isSameList } from '../../../dto/data-model-utils.js';
import { SeatingLayout } from '../../seating/seating-layout.js';
import { SeatPreviewState } from '../../seating-preview/seat-preview-state.js';
import { JSONUtils } from '../../../../utility/json-utils.js';

export class InitializePreviewResponsePayload extends Payload {
  static readonly TYPE_ID = 'bdcaa2170cd24b20b7e2e135287a1576';

  private constructor(
    public readonly target: string | null,
    public readonly eventProfile: EventProfile | null,
    public readonly seatingLayout: SeatingLayout | null,
    public readonly backgroundId: string | null,
    public readonly seatPreviewStates: SeatPreviewState[] | null,
  ) {
    super();
  }

  get $typeId(): string {
    return InitializePreviewResponsePayload.TYPE_ID;
  }

  get type(): PayloadType {
    return PayloadType.InitializePreviewResponse;
  }

  static create(args: {
    target: string | null;
    eventProfile: EventProfile | null;
    seatingLayout: SeatingLayout | null;
    backgroundId: string | null;
    seatPreviewStates: SeatPreviewState[] | null;
  }): InitializePreviewResponsePayload {
    return new InitializePreviewResponsePayload(args.target, args.eventProfile, args.seatingLayout, args.backgroundId, args.seatPreviewStates);
  }

  static fromMap(context: SerializationContext, map: SerializedData): InitializePreviewResponsePayload {
    return InitializePreviewResponsePayload.create({
      target: MapUtils.getStringOrNull(map, 'target'),
      eventProfile: context.fromDataOrNull<EventProfile>(MapUtils.getRecordOrNull(map, 'eventProfile')),
      seatingLayout: withBase64Layout(context, () => context.fromDataOrNull<SeatingLayout>(MapUtils.getRecordOrNull(map, 'seatingLayout'))),
      backgroundId: MapUtils.getStringOrNull(map, 'backgroundId'),
      seatPreviewStates: MapUtils.getListOrNull<SeatPreviewState>(map, 'seatPreviewStates', (value) => context.fromData<SeatPreviewState>(JSONUtils.decodeRecord(value))),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'target': this.target,
      'eventProfile': context.toDataOrNull(this.eventProfile),
      'seatingLayout': withBase64Layout(context, () => context.toDataOrNull(this.seatingLayout)),
      'backgroundId': this.backgroundId,
      'seatPreviewStates': this.seatPreviewStates?.map((value) => context.toData(value)) ?? null,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    target = this.target,
    eventProfile = cloneDataOrNull(this.eventProfile),
    seatingLayout = cloneDataOrNull(this.seatingLayout),
    backgroundId = this.backgroundId,
    seatPreviewStates = this.seatPreviewStates == null ? null : cloneListOfCloneable(this.seatPreviewStates),
  }: {
    target?: string | null;
    eventProfile?: EventProfile | null;
    seatingLayout?: SeatingLayout | null;
    backgroundId?: string | null;
    seatPreviewStates?: SeatPreviewState[] | null;
  } = {}): this {
    return InitializePreviewResponsePayload.create({ target, eventProfile, seatingLayout, backgroundId, seatPreviewStates }) as this;
  }

  isSame(other: InitializePreviewResponsePayload): boolean {
    return this === other || (
      this.target === other.target &&
      isSameData(this.eventProfile, other.eventProfile) &&
      isSameData(this.seatingLayout, other.seatingLayout) &&
      this.backgroundId === other.backgroundId &&
      isSameList(this.seatPreviewStates, other.seatPreviewStates, isSameData)
    );
  }
}

export class InitializePreviewResponsePayloadBuilder extends DataModelBuilder<InitializePreviewResponsePayload> {
  build(context: SerializationContext, map: SerializedData): InitializePreviewResponsePayload {
    return InitializePreviewResponsePayload.fromMap(context, map);
  }
}

function withBase64Layout<T>(context: SerializationContext, operation: () => T): T {
  const hadFormat = context.storage.has('format');
  const previous = context.storage.get<unknown>('format');
  context.storage.set('format', 'base64');
  try {
    return operation();
  }
  finally {
    if(hadFormat) context.storage.set('format', previous);
    else context.storage.remove('format');
  }
}
